import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button, ErrorMessage, Field, Input, Label } from '@ankor-io/ui-components'

const feeSchema = z.string().refine(
  (val) => {
    if (val === '') return false
    const num = parseFloat(val)
    return !isNaN(num) && num >= 0 && num <= 100
  },
  { message: 'Must be a number between 0 and 100' },
)

const formSchema = z.object({
  conversionFee: feeSchema,
  transferFee: feeSchema,
})

type FormData = z.infer<typeof formSchema>

export type OrganizationFeesFormProps = {
  useGlobalDefaults: boolean
  conversionFeeValue: string
  transferFeeValue: string
  onConversionFeeChange: (value: string) => void
  onTransferFeeChange: (value: string) => void
  onSubmit: () => void
  hasChanges: boolean
  isSubmitting: boolean
}

export function OrganizationFeesForm({
  useGlobalDefaults,
  conversionFeeValue,
  transferFeeValue,
  onConversionFeeChange,
  onTransferFeeChange,
  onSubmit,
  hasChanges,
  isSubmitting,
}: OrganizationFeesFormProps) {
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      conversionFee: conversionFeeValue,
      transferFee: transferFeeValue,
    },
  })

  // Sync external values with form state
  useEffect(() => {
    form.setValue('conversionFee', conversionFeeValue, { shouldValidate: true })
  }, [conversionFeeValue, form])

  useEffect(() => {
    form.setValue('transferFee', transferFeeValue, { shouldValidate: true })
  }, [transferFeeValue, form])

  const handleSubmit = (data: FormData) => {
    onConversionFeeChange(data.conversionFee)
    onTransferFeeChange(data.transferFee)
    onSubmit()
  }

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4 w-full flex-1">
            <Field>
              <Label className="text-sm/6 font-medium text-gray-700 dark:text-gray-300">Conversion Fee (%)</Label>
              <Input
                type="text"
                inputMode="decimal"
                disabled={useGlobalDefaults}
                readOnly={useGlobalDefaults}
                {...form.register('conversionFee', {
                  onChange: (e) => onConversionFeeChange(e.target.value),
                })}
              />
              {form.formState.errors.conversionFee && (
                <ErrorMessage>{form.formState.errors.conversionFee.message}</ErrorMessage>
              )}
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4 w-full flex-1">
            <Field>
              <Label className="text-sm/6 font-medium text-gray-700 dark:text-gray-300">Transfer Fee (%)</Label>
              <Input
                type="text"
                inputMode="decimal"
                disabled={useGlobalDefaults}
                readOnly={useGlobalDefaults}
                {...form.register('transferFee', {
                  onChange: (e) => onTransferFeeChange(e.target.value),
                })}
              />
              {form.formState.errors.transferFee && (
                <ErrorMessage>{form.formState.errors.transferFee.message}</ErrorMessage>
              )}
            </Field>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={!hasChanges || isSubmitting || !form.formState.isValid}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </form>
  )
}
